import type { AccountView } from "@kwp/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId } from "react";
import { errorMessage, useTRPC } from "../../lib/api";
import { useMe } from "../../lib/me";
import { ErrorText, Section } from "../../ui";
import { PROVIDER_LABEL } from "./accounts-model";

/**
 * "Default From for <workspace>" (architecture §1, §8): which of the person's own mailboxes new
 * mail in a workspace is sent from. The choice is theirs alone (`users/{uid}.defaultFrom`, written
 * by `workspaces.updateSettings`): members cannot send from each other's accounts, so it is not a
 * shared workspace setting. Without a choice the composer starts from the first mailbox in the
 * workspace.
 */
export function DefaultFromSection({ accounts }: { accounts: readonly AccountView[] }) {
  const me = useMe();
  if (accounts.length === 0) return null;
  return (
    <Section title="Default From" description="The mailbox new mail starts from in each workspace. Only you see and use this choice.">
      <ul className="flex flex-col gap-3">
        {me.workspaces.map((w) => (
          <li key={w.id}>
            <DefaultFromPicker workspace={w} accounts={accounts} />
          </li>
        ))}
      </ul>
    </Section>
  );
}

const AUTOMATIC = "";

function DefaultFromPicker({ workspace, accounts }: { workspace: { id: string; name: string }; accounts: readonly AccountView[] }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const id = useId();
  const key = trpc.workspaces.get.queryKey({ workspaceId: workspace.id });
  const current = useQuery(trpc.workspaces.get.queryOptions({ workspaceId: workspace.id }));
  const update = useMutation(trpc.workspaces.updateSettings.mutationOptions({ onSuccess: (view) => queryClient.setQueryData(key, view) }));
  const chosen = current.data === undefined ? null : current.data.defaultFromAccountId;
  // A choice whose mailbox was disconnected since shows as automatic (the composer ignores it too).
  const value = chosen !== null && accounts.some((a) => a.id === chosen) ? chosen : AUTOMATIC;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-text">
        Default From for {workspace.name}
      </label>
      <select
        id={id}
        value={value}
        disabled={!current.isSuccess || update.isPending}
        onChange={(e) => update.mutate({ workspaceId: workspace.id, defaultFromAccountId: e.target.value === AUTOMATIC ? null : e.target.value })}
        className="min-h-9 w-full max-w-md rounded-md border border-border bg-surface px-2 text-sm text-text"
      >
        <option value={AUTOMATIC}>Automatic (first mailbox in the workspace)</option>
        {accounts.map((a) => (
          <option key={a.id} value={a.id}>
            {a.address} ({PROVIDER_LABEL[a.provider]})
          </option>
        ))}
      </select>
      {current.isError ? <ErrorText>{errorMessage(current.error)}</ErrorText> : null}
      {update.isError ? <ErrorText>{errorMessage(update.error)}</ErrorText> : null}
    </div>
  );
}
